/**
 * Developer Onboarding Permission Profile Management
 *
 * Scripts to create, read, and delete permission profiles for the developer onboarding orchestrator
 */

const PERMISSION_PROFILE_USER_TYPE = "developer_onboarding_permissions";

function generateCollectionsPermissions(userTypes, ctx) {
    return userTypes.map(userType => ({
        _actions: [
            "READ",
            "EDIT",
            "CREATE",
            "SHARE",
            "DELETE"
        ],
        _namespace: ctx?._namespaces?.[0],
        _resourceDesc: {
            _irn: "itemsvc:nameduseritem:*",
            _criteria: {
                _userType: userType
            }
        }
    }))
}

function generateOrchestratorPermissions(userTypes, ctx) {
    return userTypes.map(userType => ({
        _actions: [
            "READ",
            "RUN"
        ],
        _namespace: ctx?._namespaces?.[0],
        _resourceDesc: {
            _irn: "datasourcesvc:orchestrator:*",
            _criteria: {
                _userType: userType
            }
        }
    }))
}

/**
 * Create or update permission profile for developer onboarding orchestrator
 *
 * This profile grants READ and CREATE permissions on organization and application resources
 */
async function createDeveloperOnboardingPermissionProfile(input, libraries, ctx) {
    const { IafPassSvc } = libraries.PlatformApi;

    const profileCriteria = {
        _userType: PERMISSION_PROFILE_USER_TYPE
    };

    const permissions = [
        {
            _actions: ["CREATE"],
            _namespace: ctx._namespaces[0],
            _resourceDesc: {
                _irn: "passportsvc:organization:*",
            }
        },
        {
            _actions: ["CREATE"],
            _namespace: ctx._namespaces[0],
            _resourceDesc: {
                _irn: "passportsvc:application:*",
            }
        },
        {
            _actions: [
                "READ",
            ],
            _namespace: ctx?._namespaces?.[0],
            _resourceDesc: {
                _irn: "itemsvc:nameduseritem:*",
                _criteria: {
                    _itemClass: "Script"
                }
            }
        },
        ...generateCollectionsPermissions(["twinit-apps", "apps", "orgs", "twinit-orgs"], ctx),
        ...generateOrchestratorPermissions(["create_sandbox_app", "developer_onboarding"], ctx)
    ];

    // Check if profile already exists
    let profileResponse = await IafPassSvc.getPermissionProfiles(profileCriteria, ctx);
    let profile = profileResponse._list?.[0];

    if (!profile) {
        // Create new profile
        const profileToCreate = {
            ...profileCriteria,
            _name: "Developer Onboarding Orchestrator Permissions",
            _namespaces: ctx._namespaces,
            _permissions: permissions
        };

        profileResponse = await IafPassSvc.createPermissionProfiles([profileToCreate], ctx);
        profile = profileResponse._list[0];

        return {
            success: true,
            action: "created",
            profile: profile,
            profileId: profile._id,
            message: "Permission profile created successfully"
        };
    } else {
        // Update existing profile with latest permissions
        const profileToUpdate = {
            ...profile,
            _permissions: permissions
        };

        await IafPassSvc.updatePermissionProfile(profile._id, profileToUpdate, ctx);

        return {
            success: true,
            action: "updated",
            profile: profile,
            profileId: profile._id,
            message: "Permission profile updated successfully"
        };
    }
}

/**
 * Get the developer onboarding permission profile
 */
async function getDeveloperOnboardingPermissionProfile(input, libraries, ctx) {
    const { IafPassSvc } = libraries.PlatformApi;

    const profileCriteria = {
        _userType: PERMISSION_PROFILE_USER_TYPE
    };

    try {
        const profileResponse = await IafPassSvc.getPermissionProfiles(profileCriteria, ctx);
        const profile = profileResponse._list?.[0];

        if (profile) {
            return {
                success: true,
                exists: true,
                profile: profile,
                profileId: profile._id
            };
        } else {
            return {
                success: true,
                exists: false,
                message: "Permission profile not found"
            };
        }
    } catch (err) {
        return {
            success: false,
            exists: false,
            error: err.message
        };
    }
}

/**
 * Delete the developer onboarding permission profile
 */
async function deleteDeveloperOnboardingPermissionProfile(input, libraries, ctx) {
    const { IafPassSvc } = libraries.PlatformApi;

    const profileCriteria = {
        _userType: PERMISSION_PROFILE_USER_TYPE
    };

    try {
        const profileResponse = await IafPassSvc.getPermissionProfiles(profileCriteria, ctx);
        const profile = profileResponse._list?.[0];

        if (profile) {
            await IafPassSvc.deletePermissionProfile(profile._id, ctx);

            return {
                success: true,
                deleted: true,
                profileId: profile._id,
                message: "Permission profile deleted successfully"
            };
        } else {
            return {
                success: true,
                deleted: false,
                message: "Permission profile not found"
            };
        }
    } catch (err) {
        return {
            success: false,
            error: err.message
        };
    }
}

const scriptModule = {
    createDeveloperOnboardingPermissionProfile,
    getDeveloperOnboardingPermissionProfile,
    deleteDeveloperOnboardingPermissionProfile
};

export default scriptModule;
